import { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { Button } from "./ui/button";

/**
 * PWA install prompt — captures `beforeinstallprompt` event and shows
 * a friendly banner to the user. Auto-hides after install or dismiss.
 */
const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Don't show if already dismissed in this session or installed
    if (sessionStorage.getItem("pwa_dismissed") === "1") return;
    if (window.matchMedia("(display-mode: standalone)").matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Show after 5s so it doesn't interrupt onboarding
      setTimeout(() => setVisible(true), 5000);
    };
    window.addEventListener("beforeinstallprompt", handler);

    // Hide if app gets installed
    const installedHandler = () => {
      setVisible(false);
      setDeferredPrompt(null);
    };
    window.addEventListener("appinstalled", installedHandler);

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
      window.removeEventListener("appinstalled", installedHandler);
    };
  }, []);

  const install = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    setVisible(false);
  };

  const dismiss = () => {
    sessionStorage.setItem("pwa_dismissed", "1");
    setVisible(false);
  };

  if (!visible || !deferredPrompt) return null;

  return (
    <div
      className="fixed bottom-24 md:bottom-6 left-4 right-4 md:left-auto md:right-6 md:max-w-sm z-50 animate-page-in"
      data-testid="pwa-install-prompt"
    >
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-2xl shadow-2xl p-4 flex items-center gap-3">
        <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0">
          <Download className="w-6 h-6" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="font-bold text-sm">Instalar o GoFood</p>
          <p className="text-xs text-white/90">Acesso rápido direto da tela inicial</p>
        </div>
        <div className="flex gap-1 shrink-0">
          <Button
            onClick={install}
            size="sm"
            className="bg-white text-orange-600 hover:bg-orange-50"
            data-testid="pwa-install-btn"
          >
            Instalar
          </Button>
          <Button
            onClick={dismiss}
            size="sm"
            variant="ghost"
            className="text-white hover:bg-white/10 px-2"
            data-testid="pwa-dismiss-btn"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
